import { useQueryClient, type Query } from '@tanstack/react-query';
import { TASK_KEYS } from './queryKeys';
import type { Task } from '../types';

function isCursorQuery(query: Query, orgId: string): boolean {
  const key = query.queryKey as readonly unknown[];
  return (
    key.length >= 4 &&
    key[0] === 'tasks' &&
    key[1] === orgId &&
    (key[3] === 'query' || key[3] === 'groupPage')
  );
}

function isGanttQuery(query: Query, orgId: string): boolean {
  const key = query.queryKey as readonly unknown[];
  return key.length >= 4 && key[0] === 'tasks' && key[1] === orgId && key[3] === 'gantt';
}

function isCalendarQuery(query: Query, orgId: string): boolean {
  const key = query.queryKey as readonly unknown[];
  return key.length >= 4 && key[0] === 'tasks' && key[1] === orgId && key[3] === 'calendar';
}

function isSubtasksQuery(query: Query, orgId: string): boolean {
  const key = query.queryKey as readonly unknown[];
  return key.length >= 4 && key[0] === 'tasks' && key[1] === orgId && key[2] === 'subtasks';
}

/**
 * Writes an array of tasks into the normalized entity cache.
 */
export function seedTaskEntities(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  tasks: Task[],
): void {
  for (const task of tasks) {
    queryClient.setQueryData<Task>(TASK_KEYS.entity(orgId, task.id), task);
  }
}

/**
 * Updates a single entity and propagates the change to all active collection queries.
 */
export function updateTaskEntity(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  taskId: string,
  updater: (task: Task) => Task,
): void {
  // 1. Update entity cache
  queryClient.setQueryData<Task>(TASK_KEYS.entity(orgId, taskId), (old) => {
    if (!old) return old;
    return updater(old);
  });

  // 2. Propagate to cursor-based queries (list view, calendar, kanban per-column)
  queryClient.setQueriesData<{ pages: { items: Task[] }[] }>(
    { predicate: (query) => isCursorQuery(query, orgId) },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          items: page.items.map((t) => (t.id === taskId ? updater(t) : t)),
        })),
      };
    },
  );

  // 3. Invalidate gantt queries — shape is TaskSummaryDto[], refetch instead of patch
  queryClient.invalidateQueries({
    predicate: (query) => isGanttQuery(query, orgId),
  });

  // 4. Patch flat calendar queries (Task[])
  queryClient.setQueriesData<Task[]>(
    { predicate: (query) => isCalendarQuery(query, orgId) },
    (old) => {
      if (!Array.isArray(old)) return old;
      return old.map((t) => (t.id === taskId ? updater(t) : t));
    },
  );

  // 5. Patch flat subtask-list queries (Task[]) — the expanded-row subtask list and
  // the detail dialog's subtask list both read this key directly, not through the
  // cursor-based collections patched above.
  queryClient.setQueriesData<Task[]>(
    { predicate: (query) => isSubtasksQuery(query, orgId) },
    (old) => {
      if (!Array.isArray(old)) return old;
      return old.map((t) => (t.id === taskId ? updater(t) : t));
    },
  );
}

/**
 * Removes an entity from cache and all collections.
 */
export function removeTaskEntity(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  taskId: string,
): void {
  queryClient.removeQueries({ queryKey: TASK_KEYS.entity(orgId, taskId) });
  queryClient.removeQueries({ queryKey: TASK_KEYS.detail(orgId, taskId) });

  queryClient.setQueriesData<{ pages: { items: Task[] }[] }>(
    { predicate: (query) => isCursorQuery(query, orgId) },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          items: page.items.filter((t) => t.id !== taskId),
        })),
      };
    },
  );

  queryClient.setQueriesData<Task[]>(
    { predicate: (query) => isCalendarQuery(query, orgId) },
    (old) => {
      if (!Array.isArray(old)) return old;
      return old.filter((t) => t.id !== taskId);
    },
  );

  queryClient.setQueriesData<Task[]>(
    { predicate: (query) => isSubtasksQuery(query, orgId) },
    (old) => {
      if (!Array.isArray(old)) return old;
      return old.filter((t) => t.id !== taskId);
    },
  );

  queryClient.invalidateQueries({
    predicate: (query) => isGanttQuery(query, orgId),
  });
}

/**
 * Hook wrapper for entity cache utilities.
 */
export function useTaskEntityCache() {
  const queryClient = useQueryClient();

  const seed = (orgId: string, tasks: Task[]) => seedTaskEntities(queryClient, orgId, tasks);

  const update = (orgId: string, taskId: string, updater: (task: Task) => Task) =>
    updateTaskEntity(queryClient, orgId, taskId, updater);

  const remove = (orgId: string, taskId: string) => removeTaskEntity(queryClient, orgId, taskId);

  return { seed, update, remove };
}
